import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Carousel } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import 'bootstrap/dist/css/bootstrap.min.css';
import './CarouselComponent.css';
import { checkAndUpdateLanguage } from '../i18n/i18n';

// Import all slider data files
import sliderDataEn from './en.json';
import sliderDataSc from './sc.json';
import sliderDataTc from './tc.json';
import sliderDataTh from './th.json';

const HeroSlider = () => {
  const { i18n } = useTranslation();
  const [sliderData, setSliderData] = useState([]);
  const videoRefs = useRef({});

  const loadSliderData = useCallback(() => {
    const language = checkAndUpdateLanguage(i18n.language);
    let data;

    switch (language) {
      case 'en':
        data = sliderDataEn;
        break;
      case 'sc':
        data = sliderDataSc;
        break;
      case 'tc':
        data = sliderDataTc;
        break;
      case 'th':
        data = sliderDataTh;
        break;
      default:
        data = sliderDataEn; // fallback to English
    }

    // Ensure data is an array
    setSliderData(Array.isArray(data) ? data : Object.values(data));
  }, [i18n.language]);

  useEffect(() => {
    loadSliderData();

    const handleLanguageChange = () => {
      loadSliderData();
    };

    window.addEventListener('languageChanged', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [i18n.language, loadSliderData]);

  // Function to determine media type based on file extension
  const getMediaType = (src) => {
    if (!src) return 'image';
    
    const extension = src.split('.').pop().toLowerCase();
    if (extension === 'mp4') return 'video';
    if (extension === 'gif') return 'gif';
    return 'image';
  };

  // Handle slide change
  const handleSlideChange = (selectedIndex) => {
    // Play the current slide's video if it exists
    const currentVideo = videoRefs.current[selectedIndex];
    if (currentVideo && getMediaType(sliderData[selectedIndex]?.image) === 'video') {
      currentVideo.play().catch(err => console.log("Auto-play prevented"));
    }
  };

  // Add global interaction handler to trigger video playback
  useEffect(() => {
    const playAllVideos = () => {
      Object.values(videoRefs.current).forEach(video => {
        if (video) {
          video.play().catch(e => {});
        }
      });
    };

    // iOS requires user interaction
    document.addEventListener('touchstart', playAllVideos, { once: true });
    document.addEventListener('click', playAllVideos, { once: true });

    return () => {
      document.removeEventListener('touchstart', playAllVideos);
      document.removeEventListener('click', playAllVideos);
    };
  }, []);

  // Function to render the appropriate media element
  const renderMedia = (slide, index) => {
    const mediaType = getMediaType(slide.image);
    
    switch (mediaType) {
      case 'video':
        return (
          <video 
            ref={el => { videoRefs.current[index] = el; }}
            className="d-block w-100"
            autoPlay 
            loop 
            muted 
            playsInline
            preload={index === 0 ? "auto" : "metadata"} // Only preload first video
            webkit-playsinline="true"
            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
          >
            <source src={slide.image} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        );
      case 'gif':
      case 'image':
      default:
        return (
          <img 
            src={slide.image} 
            alt={slide.title}
            className="d-block w-100" 
            loading={index === 0 ? "eager" : "lazy"} // Lazy load non-first images
            fetchpriority={index === 0 ? "high" : "low"} // Prioritize first image
            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
          />
        );
    }
  };

  return (
    <Carousel 
      className="hero-slider" 
      interval={5000}
      onSelect={handleSlideChange}
    >
      {Array.isArray(sliderData) && sliderData.map((slide, index) => (
        <Carousel.Item key={index} className="hero-slide">
          {renderMedia(slide, index)}
          <Carousel.Caption className="hero-text">
            <h3 className="main-text">{slide.title}</h3>
            <p className="sub-text1">{slide.description}</p>
            {slide.additionalText && (
              <p className="sub-text2">{slide.additionalText}</p>
            )}
          </Carousel.Caption>
        </Carousel.Item>
      ))}
    </Carousel>
  );
};

export default HeroSlider;